import React, { useEffect, useRef, useState } from 'react';
import {
  fetchFieldCatalog,
  parseTemplate,
  buildTemplate,
  type CatalogField,
  type CatalogGroup,
  type TemplateBlock,
  type SaisieField,
} from '../api/templateBuilder';

// La valeur est le sous-dossier de rangement du modèle : la changer déplace
// les futurs enregistrements, jamais ceux déjà en service.
const CATEGORIES = [
  { value: 'attestation', label: 'Attestation' },
  { value: 'contrat-cdd', label: 'Contrat CDD' },
  { value: 'contrat-cdi', label: 'Contrat CDI' },
  { value: 'solde-tout-compte', label: 'Solde de tout compte' },
  { value: 'fin-contrat', label: 'Fin de contrat' },
  { value: 'avenants', label: 'Avenants' },
  { value: 'certificats', label: 'Certificats' },
  { value: 'autres', label: 'Autres' },
];

interface Props {
  /** Appelé après un enregistrement réussi, pour rafraîchir la liste des documents. */
  onSaved?: () => void;
}

/**
 * Composeur de modèles, intégré à la page Documents.
 *
 * On importe un .docx, on remplace dans son texte les valeurs figées par des
 * champs de la fiche employé ou par des marqueurs de saisie, et on l'enregistre :
 * le modèle rejoint alors « Génération de Documents ».
 */
const TemplateComposer: React.FC<Props> = ({ onSaved }) => {
  const [catalog, setCatalog] = useState<CatalogGroup[]>([]);
  const [stagingId, setStagingId] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<TemplateBlock[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0].value);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ path: string; fields: SaisieField[] } | null>(null);

  // Paragraphe ayant le focus : une insertion depuis le catalogue s'y applique.
  const focusedBlock = useRef<number | null>(null);
  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchFieldCatalog().then(setCatalog).catch(() => setCatalog([]));
  }, []);

  /** Clic sur un champ du catalogue : demande l'intitulé si le champ est libre. */
  const choisirChamp = (champ: CatalogField) => {
    if (!champ.ask) {
      insertCode(champ.code);
      return;
    }
    const intitule = demanderIntitule();
    if (intitule) insertCode(champ.code.replace('%s', intitule));
  };

  const reset = () => {
    setStagingId(null);
    setBlocks([]);
    setError(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setSaved(null);
    setBusy(true);
    try {
      const result = await parseTemplate(file);
      setStagingId(result.staging_id);
      setBlocks(result.blocks);
      setName(result.original_name);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Impossible de lire ce document.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Intitulé d'un champ libre, demandé avant insertion.
   *
   * Sans cela, tous les champs libres d'un modèle portaient le même nom
   * générique : à la génération, des mois plus tard, plus personne ne savait
   * ce que chacun attendait. Retourne null si l'utilisateur renonce.
   */
  const demanderIntitule = (): string | null => {
    const saisi = window.prompt(
      "Quel est l'intitulé de cette information ?\n" +
      "Il sera affiché au moment de générer le document.\n" +
      "Exemple : Motif de l'absence, Numéro de facture…"
    );
    if (saisi === null) return null;
    // Ni accolades ni deux-points : ils casseraient le marqueur.
    const propre = saisi.replace(/[{}:]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!propre) {
      setError("L'intitulé ne peut pas être vide : sans lui, le champ serait impossible à reconnaître.");
      return null;
    }
    return propre;
  };

  /** Insère un placeholder à la position du curseur du paragraphe actif. */
  const insertCode = (code: string) => {
    const i = focusedBlock.current;
    if (i === null) {
      setError("Cliquez d'abord dans le paragraphe où insérer le champ.");
      return;
    }
    setError(null);
    const area = textareaRefs.current[i];
    const bloc = blocks.find(b => b.i === i);
    if (!bloc) return;

    const debut = area?.selectionStart ?? bloc.text.length;
    const fin = area?.selectionEnd ?? bloc.text.length;
    const texte = bloc.text.slice(0, debut) + code + bloc.text.slice(fin);
    setBlocks(prev => prev.map(b => (b.i === i ? { ...b, text: texte } : b)));

    requestAnimationFrame(() => {
      const el = textareaRefs.current[i];
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = debut + code.length;
      }
    });
  };

  const handleSave = async (overwrite = false) => {
    if (!stagingId) return;
    if (!name.trim()) {
      setError('Donnez un nom au modèle.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await buildTemplate({ staging_id: stagingId, name, category, blocks, overwrite });
      setSaved({ path: result.relative_path, fields: result.fields });
      reset();
      onSaved?.();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.exists) {
        if (window.confirm(`${data.error}\n\nRemplacer le modèle existant ?`)) {
          setBusy(false);
          return handleSave(true);
        }
      } else {
        setError(data?.error || 'Enregistrement impossible.');
      }
    } finally {
      setBusy(false);
    }
  };

  const nbPlaceholders = blocks.reduce((n, b) => n + (b.text.match(/\{\{/g) || []).length, 0);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-base-300 overflow-hidden">
      <div className="p-6 flex items-start gap-4">
        <div className="p-3 bg-warning/15 rounded-xl shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold">Composer un modèle</h2>
          <p className="text-sm text-base-content/70 mt-1 max-w-3xl">
            Importez un document Word, remplacez les valeurs figées par des champs de la fiche
            employé ou par des informations à saisir à la génération. Le modèle rejoint
            automatiquement « Génération de Documents ».
          </p>
        </div>
      </div>

      <div className="px-6 pb-6">
        {error && (
          <div className="alert alert-error mb-4 py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {saved && (
          <div className="alert alert-success mb-4 flex-col items-start py-3">
            <span className="font-semibold text-sm">Modèle enregistré : {saved.path}</span>
            <span className="text-sm">
              Il est disponible dans « Génération de Documents » sur la fiche d'un employé.
              {saved.fields.length > 0 && (
                <> Champs demandés à la génération : <strong>{saved.fields.map(f => f.label).join(', ')}</strong>.</>
              )}
            </span>
          </div>
        )}

        {/* Étape 1 : import */}
        {!stagingId && (
          <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-xl border-2 border-dashed border-base-300 bg-base-200/40">
            <div className="text-3xl">📄</div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-semibold">Importer un document Word</div>
              <div className="text-xs text-base-content/60">Format accepté : .docx</div>
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".docx"
              className="file-input file-input-bordered file-input-warning w-full sm:w-auto max-w-sm"
              disabled={busy}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {busy && <span className="loading loading-spinner loading-md text-warning" />}
          </div>
        )}

        {/* Étape 2 : composition */}
        {stagingId && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 flex flex-col gap-4">
              <div>
                <div className="font-semibold mb-1">Contenu du document</div>
                <p className="text-xs text-base-content/60 mb-2">
                  Cliquez dans un paragraphe, puis choisissez un champ à droite pour l'insérer
                  à l'endroit du curseur.
                </p>
                <div className="flex flex-col gap-3 max-h-[55vh] overflow-y-auto pr-1">
                  {blocks.map(bloc => (
                    <textarea
                      key={bloc.i}
                      ref={(el) => { textareaRefs.current[bloc.i] = el; }}
                      className="textarea textarea-bordered w-full text-sm leading-relaxed bg-white"
                      rows={Math.min(6, Math.max(2, Math.ceil(bloc.text.length / 70)))}
                      value={bloc.text}
                      onFocus={() => { focusedBlock.current = bloc.i; }}
                      onChange={(e) =>
                        setBlocks(prev =>
                          prev.map(b => (b.i === bloc.i ? { ...b, text: e.target.value } : b))
                        )
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="border-t border-base-300 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="form-control">
                    <span className="label-text mb-1">Nom du modèle</span>
                    <input
                      className="input input-bordered bg-white"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Attestation de travail"
                    />
                  </label>
                  <label className="form-control">
                    <span className="label-text mb-1">Catégorie</span>
                    <select
                      className="select select-bordered bg-white"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-xs text-base-content/60">
                    {nbPlaceholders} champ{nbPlaceholders > 1 ? 's' : ''} inséré{nbPlaceholders > 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-2">
                    <button className="btn btn-ghost btn-sm" onClick={reset}>Annuler</button>
                    <button
                      className="btn btn-warning text-white border-none"
                      disabled={busy}
                      onClick={() => handleSave()}
                    >
                      {busy ? <span className="loading loading-spinner loading-sm" /> : 'Enregistrer le modèle'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Catalogue des champs */}
            <div className="rounded-xl border border-base-300 bg-base-200/40 p-4 h-fit">
              <div className="font-semibold">Champs disponibles</div>
              <p className="text-xs text-base-content/60 mb-3">
                Les champs employé sont remplis automatiquement. Ceux de la dernière rubrique
                seront demandés à chaque génération.
              </p>
              <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
                {catalog.map(groupe => (
                  <div key={groupe.group}>
                    <div className="text-xs font-bold uppercase text-base-content/50 mb-1">
                      {groupe.group}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {groupe.fields.map(champ => (
                        <button
                          key={champ.code}
                          type="button"
                          className="btn btn-xs btn-outline bg-white"
                          title={champ.code}
                          onClick={() => choisirChamp(champ)}
                        >
                          {champ.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateComposer;
